import type { ReactNode } from "react";
import { api, type Item } from "../api";

/**
 * Tour content. Every label below is copied from the real screen it describes;
 * if you rename a button or field, update the matching step. `target` names a
 * `data-tour="…"` attribute on the real element to highlight; `path` is the real
 * page to load (or a function that finds one, e.g. the first item to open).
 */
export interface TourStep {
  title: string;
  body: ReactNode;
  path?: string | (() => Promise<string>);
  target?: string;
}

export interface Track {
  id: string;
  /** Shown as the card's eyebrow and on "Show me: …" buttons. */
  label: string;
  steps: TourStep[];
}

const firstItem = async (query: string, base: string, fallback: string): Promise<string> => {
  try {
    const r = await api<{ items: Item[] }>(`/items?${query}&limit=1`);
    return r.items[0] ? `${base}/${r.items[0].number}` : fallback;
  } catch {
    return fallback;
  }
};

const firstCustomer = async (): Promise<string> => {
  try {
    const r = await api<{ orgs: Array<{ id: string; archived: number }> }>("/staff/orgs");
    const org = r.orgs.find((o) => !o.archived);
    return org ? `/staff/customers/${org.id}` : "/staff/customers";
  } catch {
    return "/staff/customers";
  }
};

const replayNote = (
  <p>
    You can replay this any time from <strong>Your account</strong>: click your name at the top right.
  </p>
);

// --- Staff -------------------------------------------------------------------

const STAFF_MAIN: Track = {
  id: "main",
  label: "Welcome tour",
  steps: [
    {
      title: "Welcome to Departure Labs Support",
      path: "/staff",
      body: (
        <>
          <p>This is where all of our customer work comes together: tickets customers send us, requests from inside their apps, our own tasks, and GitHub issues.</p>
          <p>Everything is ranked in one list, so anyone at Departure Labs can see what matters most. This tour takes about two minutes.</p>
        </>
      ),
    },
    {
      title: "Dashboard",
      path: "/staff",
      target: "nav-dashboard",
      body: (
        <>
          <p>Your starting point each day.</p>
          <ul>
            <li><strong>Ask the queue</strong>: ask questions in plain English, like "What should I work on next?"</li>
            <li><strong>Top of the queue</strong>: the ten most important open items right now.</li>
            <li><strong>Customer health</strong>: which customers need attention, worst first.</li>
            <li><strong>In-flight projects</strong> and the <strong>Development backlog</strong> (open GitHub issues).</li>
          </ul>
        </>
      ),
    },
    {
      title: "Queue",
      path: "/staff/queue",
      target: "nav-queue",
      body: (
        <>
          <p>All open work in one ranked list. Each row is one of four kinds:</p>
          <ul>
            <li><strong>Ticket</strong>: a customer asked for help in this portal.</li>
            <li><strong>In-app</strong>: feedback sent from inside a customer's app.</li>
            <li><strong>Task</strong>: internal work we created.</li>
            <li><strong>GitHub</strong>: an issue from a Departure-Labs repo.</li>
          </ul>
          <p>
            <strong>Unsorted</strong> holds requests from outside (like email to support@) whose sender we couldn't match to a customer; pick the customer there.
          </p>
          <p>
            Priority runs <strong>P0 Urgent</strong> to <strong>P3 Low</strong>; <strong>Triage</strong> means nobody has set one yet. The <strong>Score</strong> column decides the
            order: priority, age, customer tier and due dates. Items marked <strong>Waiting on customer</strong> drop down until they reply.
          </p>
        </>
      ),
    },
    {
      title: "Customers",
      path: "/staff/customers",
      target: "nav-customers",
      body: (
        <>
          <p>Every customer company, sorted by health so the ones that need attention are on top.</p>
          <p>
            Health is a score from 0 to 100 based on their open tickets and in-app requests (urgent ones, ones nobody has touched lately, how long the oldest has waited) and
            their projects. <strong>Healthy</strong> is 80+, <strong>Watch</strong> is 50–79, <strong>At risk</strong> is under 50. The <strong>Why</strong> column says what's
            pulling a score down.
          </p>
        </>
      ),
    },
    {
      title: "Projects",
      path: "/staff/projects",
      target: "nav-projects",
      body: (
        <p>
          Bigger pieces of work for a customer (or for us). Set a status (<strong>Planned</strong>, <strong>Active</strong>, <strong>Blocked</strong>, <strong>Done</strong>), an owner
          and a target date; progress fills in as items assigned to the project get resolved. Blocked or overdue projects lower a customer's health.
        </p>
      ),
    },
    {
      title: "Invoices",
      path: "/staff/invoices",
      target: "nav-invoices",
      body: <p>Upload a customer's invoice as a PDF and they can download it from their side of the portal. Mark it Paid or Void here when that changes.</p>,
    },
    {
      title: "Settings",
      path: "/staff/settings",
      target: "nav-settings",
      body: (
        <ul>
          <li><strong>GitHub</strong>: whether issues are syncing, and a Sync now button.</li>
          <li><strong>Discord</strong>: post new tickets, customer replies, assignments and urgent items to a channel.</li>
          <li><strong>Service keys</strong>: let other systems like Crumb use the portal's API.</li>
          <li><strong>Webhooks</strong>: send updates to other tools when items change.</li>
          <li><strong>API</strong>: everything here can also be scripted with a personal API token.</li>
        </ul>
      ),
    },
    {
      title: "You're all set",
      target: "nav-account",
      body: (
        <>
          {replayNote}
          <p>Want a hand with something specific?</p>
        </>
      ),
    },
  ],
};

const STAFF_TRIAGE: Track = {
  id: "triage",
  label: "Triage new work",
  steps: [
    {
      title: "Start with Needs triage",
      path: "/staff/queue?view=triage",
      target: "queue-tab-triage",
      body: (
        <p>
          New customer tickets and in-app requests arrive with no priority and wait here. (Each one is also emailed to support@departurelabs.io.) Click any row to open it.
        </p>
      ),
    },
    {
      title: "Set the basics",
      // First untriaged item, or failing that the top of the queue.
      path: async () => {
        const triage = await firstItem("view=triage", "/staff/items", "");
        return triage || firstItem("view=queue", "/staff/items", "/staff/queue?view=triage");
      },
      target: "triage-panel",
      body: (
        <>
          <p>Every item has a <strong>Triage</strong> panel. Changes save as soon as you pick them:</p>
          <ul>
            <li><strong>Priority</strong>: P0 Urgent to P3 Low.</li>
            <li><strong>Effort</strong>: from "1 · Under an hour" to "5 · Multi-week". This is what "quick win" questions in Ask look at.</li>
            <li><strong>Assignee</strong>, <strong>Customer</strong>, <strong>Project</strong> and <strong>Due</strong>.</li>
            <li><strong>Status</strong>: In progress, Waiting on customer, Resolved…</li>
          </ul>
        </>
      ),
    },
    {
      title: "Why it ranks where it does",
      target: "score-card",
      body: <p>The <strong>Queue score</strong> lists exactly what adds up to this item's place in the queue, so you can see why something is (or isn't) near the top.</p>,
    },
    {
      title: "Reply, or leave a note",
      target: "reply-box",
      body: (
        <p>
          A reply is emailed to the customer who reported it. Tick <strong>Internal note</strong> to write something only Departure Labs can see. Customers never see internal notes.
        </p>
      ),
    },
    {
      title: "Turn it into development work",
      target: "create-issue",
      body: (
        <p>
          <strong>Create GitHub issue</strong> opens an issue in the repo you pick, copies the description and a link back here, and links the two, so the issue shows up in the
          queue too.
        </p>
      ),
    },
    {
      title: "That's triage",
      body: <p>Work Needs triage until it's empty and the rest of the queue sorts itself.</p>,
    },
  ],
};

const STAFF_ASK: Track = {
  id: "ask",
  label: "Ask the queue",
  steps: [
    {
      title: "Ask in plain English",
      path: "/staff",
      target: "ask-box",
      body: (
        <p>
          Type a question and press <strong>Ask</strong>. The assistant reads the live queue, customer health and projects before answering. It can only look, never change
          anything.
        </p>
      ),
    },
    {
      title: "Not sure what to ask?",
      target: "ask-suggestions",
      body: (
        <p>
          Start with one of these, like "What's a quick feature I can build or modify?" Answers mention items by number (DL-1042); click a number to open it.
        </p>
      ),
    },
    {
      title: "That's Ask",
      body: <p>The more items have a priority and an effort set, the better its answers get.</p>,
    },
  ],
};

const STAFF_CUSTOMER: Track = {
  id: "customer",
  label: "Add a customer",
  steps: [
    {
      title: "Add the company",
      path: "/staff/customers",
      target: "add-customer",
      body: (
        <>
          <p>Click <strong>Add customer</strong> and fill in:</p>
          <ul>
            <li><strong>Company name</strong>.</li>
            <li><strong>Email domains</strong>: anyone with an address there can sign in and lands in this company.</li>
            <li><strong>Support tier</strong>: Priority-tier customers rank higher in the queue.</li>
          </ul>
        </>
      ),
    },
    {
      title: "Invite people outside their domain",
      path: firstCustomer,
      target: "org-people",
      body: <p>On the customer's page, <strong>Invite someone</strong> adds a person whose email isn't on the company's domains, and emails them how to sign in.</p>,
    },
    {
      title: "Connect their app",
      target: "org-apps",
      body: (
        <p>
          Under <strong>Apps &amp; feedback keys</strong>, <strong>Add app</strong> with its name (and GitHub repo, so its issues count toward this customer). Then <strong>New key</strong>:
          paste the key into that app's feedback settings so in-app requests land in the queue. The key is shown only once.
        </p>
      ),
    },
    {
      title: "That's a new customer",
      body: <p>Their people can sign in right away, and their health score appears on the Dashboard as soon as they have open work.</p>,
    },
  ],
};

// --- Customers ------------------------------------------------------------------

const CUSTOMER_MAIN: Track = {
  id: "main",
  label: "Welcome tour",
  steps: [
    {
      title: "Welcome to Departure Labs Support",
      path: "/tickets",
      body: (
        <>
          <p>This is where you ask Departure Labs for help and follow along until it's sorted.</p>
          <p>It takes about a minute to show you around.</p>
        </>
      ),
    },
    {
      title: "Tickets",
      path: "/tickets",
      target: "nav-tickets",
      body: (
        <>
          <p>Every request from your company, including ones your colleagues sent and feedback sent from inside your app. Each ticket has a status:</p>
          <ul>
            <li><strong>Open</strong>: we have it.</li>
            <li><strong>In progress</strong>: we're working on it.</li>
            <li><strong>Waiting on you</strong>: we need something from you; reply on the ticket.</li>
            <li><strong>Resolved</strong> and <strong>Closed</strong>: done.</li>
          </ul>
        </>
      ),
    },
    {
      title: "Invoices",
      path: "/invoices",
      target: "nav-invoices",
      body: <p>Your invoices from Departure Labs, ready to download as PDFs.</p>,
    },
    {
      title: "You're all set",
      target: "nav-account",
      body: (
        <>
          {replayNote}
          <p>Want a hand with something specific?</p>
        </>
      ),
    },
  ],
};

const CUSTOMER_NEW: Track = {
  id: "new-ticket",
  label: "Open a ticket",
  steps: [
    {
      title: "Start a ticket",
      path: "/tickets",
      target: "new-ticket-button",
      body: <p>Click <strong>New ticket</strong> whenever you need something.</p>,
    },
    {
      title: "Tell us what's going on",
      path: "/tickets/new",
      target: "ticket-form",
      body: (
        <ul>
          <li><strong>What kind of request?</strong> Something's broken, a question, a feature request, access, billing, or something else.</li>
          <li><strong>Short summary</strong>: one line.</li>
          <li><strong>Details</strong>: what happened and what you expected.</li>
          <li><strong>Screenshots or files</strong>: optional, up to 10 MB each.</li>
        </ul>
      ),
    },
    {
      title: "Send it",
      target: "ticket-submit",
      body: <p>Click <strong>Submit ticket</strong>. You'll get a ticket number (like DL-1042) and an email, and we'll reply in both places.</p>,
    },
    {
      title: "That's a ticket",
      body: <p>You'll find it under Tickets, with every reply from us.</p>,
    },
  ],
};

const CUSTOMER_FOLLOW: Track = {
  id: "follow-up",
  label: "Follow up on a ticket",
  steps: [
    {
      title: "Find your ticket",
      path: "/tickets",
      target: "tickets-list",
      body: <p><strong>Open</strong> shows tickets that aren't finished yet; <strong>All</strong> includes closed ones. Click a title to open it.</p>,
    },
    {
      title: "Reply or add files",
      path: () => firstItem("status=active", "/tickets", "/tickets"),
      target: "reply-box",
      body: (
        <p>
          Write in <strong>Add a reply</strong> and click <strong>Send reply</strong>, or use <strong>Attach files</strong>. If a ticket is Waiting on you or Resolved, replying
          puts it back in our queue.
        </p>
      ),
    },
    {
      title: "Done with it?",
      body: <p>When we mark a ticket resolved, you'll see <strong>Yes, close it</strong> and <strong>No, reopen</strong> at the top of the ticket. You can also close a ticket yourself at the bottom.</p>,
    },
  ],
};

export function tracksFor(role: "staff" | "customer"): Track[] {
  return role === "staff" ? [STAFF_MAIN, STAFF_TRIAGE, STAFF_ASK, STAFF_CUSTOMER] : [CUSTOMER_MAIN, CUSTOMER_NEW, CUSTOMER_FOLLOW];
}
